import {
    Box,
    Button,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    Input,
    Select,
    useDisclosure,
    useToast
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import ConfirmDialog from '../../../components/ConfirmDialog';
import FormModal from '../../../components/FormModal';
import SportSelect from '../../../components/SportSelect';
import FormField from '../../../components/fields/FormField';
import useErrorToast from '../../../hooks/useErrorToast';
import { translate } from '../../../i18n';
import { SportType } from '../../../models/court/dto/court.dto';
import { Currency } from '../../../models/court-pricing/dto/court-pricing-rule.dto';
import { CreateUserMembershipTypeDto, MembershipBillingInterval, MembershipDescriptionSection, SportDiscountDto, TimeWindowDiscountDto, UserMembershipDiscountType, UserMembershipTypeDto } from '../../../models/user-membership/dto/user-membership.dto';
import {
    EVENT_JOINING_DISCOUNT_ERROR_KEYS,
    MEMBERSHIP_DISCOUNT_ERROR_KEYS,
    validateMembershipDiscount,
} from '../utils/membership-discount';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useCreateUserMembershipType } from '../hooks/useCreateUserMembershipType';
import { useDeleteUserMembershipType } from '../hooks/useDeleteMembershipType';
import { useGetUserMembershipTypes } from '../hooks/useGetUserMembershipTypes';
import { useUpdateUserMembershipType } from '../hooks/useUpdateUserMembershipType';
import BookingParametersSegmentedControl from './BookingParametersSegmentedControl';
import { stripBlankDescriptionSections } from '../utils/description-sections';
import DescriptionSectionsEditor from './DescriptionSectionsEditor';
import MembershipTypeCguOverrideSection from './MembershipTypeCguOverrideSection';
import MembershipVariantsSection from './MembershipVariantsSection';

type Props = {
    selectedMembershipType?: UserMembershipTypeDto;
    setSelectedMembershipType: (type?: UserMembershipTypeDto) => void;
};

const initialFormData: (CreateUserMembershipTypeDto & {
    isSubscriptionBased?: boolean;
    priceInCents?: number;
    currency?: string;
    billingInterval?: MembershipBillingInterval;
}) = {
    name: '',
    description: '',
    descriptionSections: null,
    timeWindowDiscounts: [],
    coachingSessionDiscountValue: 0,
    maxCourtBookingTimeInAdvance: 7 * 24 * 60 * 60 * 1000, // 7 days in milliseconds
    coachingSessionDiscountType: UserMembershipDiscountType.PERCENTAGE,
    maxPrivateCourtBookings: 3,
    maxPrivateCourtBookingsDuration: 7 * 24 * 60 * 60 * 1000, // 7 days in milliseconds
    courtBookingDiscountType: UserMembershipDiscountType.PERCENTAGE,
    courtBookingDiscountValue: 0,
    // MYS-122. 0% = no event discount, which is what every existing membership
    // type carries after the migration — creating a type must not change that.
    eventJoiningDiscountType: UserMembershipDiscountType.PERCENTAGE,
    eventJoiningDiscountValue: 0,
    sports: null,
    sportDiscounts: null,
    isSubscriptionBased: false,
    priceInCents: undefined,
    currency: 'eur',
    billingInterval: MembershipBillingInterval.MONTHLY,
    // MYS-117 Duo: 0 = solo plan. Hydrated on edit for the same reason as the
    // event discount above — once part of formData every save sends it, so an
    // un-hydrated field would silently reset a Duo plan to solo.
    maxAttachedMembers: 0,
};

// Helper functions for price conversion
const centsToDollars = (cents: number | undefined): string => {
    if (cents === undefined || cents === null) return '';
    return (cents / 100).toFixed(2);
};

const dollarsToCents = (dollars: string): number | undefined => {
    if (!dollars || dollars === '') return undefined;
    const parsed = parseFloat(dollars);
    if (isNaN(parsed)) return undefined;
    return Math.round(parsed * 100);
};

export default function AddUserMembershipTypeModal({ selectedMembershipType, setSelectedMembershipType }: Props) {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const toast = useToast();
    const showError = useErrorToast();
    const { selectedOrganization } = useSelectedOrganization();
    const [formData, setFormData] = useState<CreateUserMembershipTypeDto>({ ...initialFormData });
    const [displayPrice, setDisplayPrice] = useState<string>('');
    const { mutateAsync: createMembershipType, isLoading: isCreating } = useCreateUserMembershipType(selectedOrganization?.id);
    const { mutateAsync: updateMembershipType, isLoading: isUpdating } = useUpdateUserMembershipType(selectedMembershipType?.id);
    const { mutateAsync: deleteMembershipType, isLoading: isDeleting } = useDeleteUserMembershipType(selectedMembershipType?.id);

    // MYS-125 — live view of the type being edited. `selectedMembershipType`
    // is a snapshot the page captured in useState when the admin clicked edit;
    // the variant CRUD hooks (useCreate/Update/DeleteMembershipVariant) patch
    // the membership-types QUERY CACHE on success, which the snapshot never
    // sees — so a just-saved variant would not appear (and `hasLiveVariants`
    // below would not react) until the modal was closed and reopened.
    // Display-derived reads must use this live row. The formData hydration
    // effect below intentionally keeps keying on the snapshot prop, so cache
    // patches and background refetches never stomp unsaved form edits.
    const { data: orgMembershipTypes } = useGetUserMembershipTypes(selectedOrganization?.id);
    const liveMembershipType = selectedMembershipType
        ? orgMembershipTypes?.find((type) => type.id === selectedMembershipType.id) ?? selectedMembershipType
        : undefined;

    useEffect(() => {
        if (selectedMembershipType) {
            setFormData({
                name: selectedMembershipType.name,
                description: selectedMembershipType.description ?? '',
                descriptionSections: selectedMembershipType.descriptionSections ?? null,
                timeWindowDiscounts: selectedMembershipType.timeWindowDiscounts.map(discount => ({
                    timeWindowGroupId: discount.timeWindowGroupId,
                    maxPrivateCourtBookings: discount.maxPrivateCourtBookings,
                    maxPrivateCourtBookingsDuration: discount.maxPrivateCourtBookingsDuration,
                    maxCourtBookingTimeInAdvance: discount.maxCourtBookingTimeInAdvance,
                    courtBookingDiscountType: discount.courtBookingDiscountType,
                    courtBookingDiscountValue: discount.courtBookingDiscountValue,
                })),
                courtBookingDiscountType: selectedMembershipType.courtBookingDiscountType,
                courtBookingDiscountValue: selectedMembershipType.courtBookingDiscountValue,
                // MYS-122 — MUST be hydrated. Once the pair is part of formData
                // every save sends it, so an un-hydrated field would show 0 for
                // a type configured at 50% and then write that 0 back on the
                // next unrelated edit. The ?? defaults only cover a dashboard
                // pointed at an API build that predates the columns, where the
                // written 0 is a no-op anyway.
                eventJoiningDiscountType: selectedMembershipType.eventJoiningDiscountType
                    ?? UserMembershipDiscountType.PERCENTAGE,
                eventJoiningDiscountValue: selectedMembershipType.eventJoiningDiscountValue ?? 0,
                sports: selectedMembershipType.sports ?? null,
                sportDiscounts: selectedMembershipType.sportDiscounts ?? null,
                coachingSessionDiscountValue: selectedMembershipType.coachingSessionDiscountValue,
                maxCourtBookingTimeInAdvance: selectedMembershipType.maxCourtBookingTimeInAdvance,
                coachingSessionDiscountType: selectedMembershipType.coachingSessionDiscountType,
                maxPrivateCourtBookings: selectedMembershipType.maxPrivateCourtBookings,
                maxPrivateCourtBookingsDuration: selectedMembershipType.maxPrivateCourtBookingsDuration,
                isSubscriptionBased: selectedMembershipType.isSubscriptionBased,
                maxAttachedMembers: selectedMembershipType.maxAttachedMembers ?? 0,
                priceInCents: selectedMembershipType.priceInCents,
                currency: selectedMembershipType.currency ?? 'eur',
                billingInterval: selectedMembershipType.billingInterval ?? MembershipBillingInterval.MONTHLY,
            });
            setDisplayPrice(centsToDollars(selectedMembershipType.priceInCents));
            onOpen();
        }
    }, [selectedMembershipType]);

    const handleInputChange = (key: keyof typeof formData, value: any) => {
        setFormData({ ...formData, [key]: value });
    };

    // Sports offerable in an override row: covered sports (or every sport when
    // unscoped) minus those already overridden — keeping the row's own sport.
    const availableOverrideSports = (current?: SportType): SportType[] => {
        const covered = formData.sports?.length ? formData.sports : (Object.values(SportType) as SportType[]);
        const taken = new Set((formData.sportDiscounts ?? []).map(d => d.sport));
        return covered.filter(sport => sport === current || !taken.has(sport));
    };

    const handlePriceChange = (value: string) => {
        setDisplayPrice(value);
        const cents = dollarsToCents(value);
        setFormData({ ...formData, priceInCents: cents });
    };

    const handleSubmit = async () => {
        try {
            const submitData = {
                ...formData,
                // MYS-27 — the editor seeds every new section and bullet with '',
                // so an operator who adds a row and doesn't fill it in would ship
                // a blank bullet that renders on the player's card as a bare
                // checkmark with no text. The API sanitizes this too (that is the
                // authoritative pass); stripping here keeps the request honest
                // and the "N bullets are behind the +" counts consistent.
                descriptionSections: stripBlankDescriptionSections(
                    formData.descriptionSections,
                ),
            };

            if (selectedMembershipType) {
                await updateMembershipType({
                    ...submitData,
                    timeWindowDiscounts: formData.timeWindowDiscounts.map(discount => {
                        // Find if this discount already existed
                        const existingDiscount = selectedMembershipType.timeWindowDiscounts.find(
                            d => d.timeWindowGroupId === discount.timeWindowGroupId
                        );

                        // If it existed, keep its ID, otherwise generate a new one
                        return existingDiscount
                            ? { ...existingDiscount, ...discount }
                            : { ...discount, id: crypto.randomUUID() } as TimeWindowDiscountDto;
                    }),
                });
            } else {
                await createMembershipType(submitData);
            }
            onModalClose();
        } catch {
            toast({ title: translate('common.error'), status: 'error', duration: 3000, isClosable: true });
        }
    };

    const onModalClose = () => {
        setFormData({ ...initialFormData });
        setDisplayPrice('');
        onClose();
        setSelectedMembershipType(undefined);
    };

    const handleDelete = async () => {
        try {
            await deleteMembershipType(selectedMembershipType);
            onDeleteClose();
            onModalClose();
        } catch (err) {
            showError(err);
        }
    };

    // MYS-125 P1.5 KNOWN GAP, closed here: once a type has a live variant, D3's
    // sync direction is variant -> type (setupMembershipTypeInStripe overwrites
    // priceInCents/currency/stripePriceId, and billingInterval when the default
    // is RECURRING, from the default variant on every Stripe sync). Editing
    // these three fields directly would silently no-op on the next sync — lock
    // them and point the operator at the variants section instead of letting
    // them believe the edit took effect.
    // Reads the LIVE row, not the snapshot: adding the first variant must lock
    // the price fields (and unlock the 0€ gate) immediately, and deleting the
    // last one must hand pricing back — not only after a modal reopen.
    const hasLiveVariants = (liveMembershipType?.variants?.length ?? 0) > 0;

    const isNameValid = Boolean(formData.name);
    // MYS-122. The API fails SAFE on a bad event discount — it stores 0 and
    // still returns 200 — so without a client-side gate an admin would get a
    // success toast for a discount that was silently thrown away.
    const eventJoiningDiscountError = validateMembershipDiscount(
        formData.eventJoiningDiscountType,
        formData.eventJoiningDiscountValue,
    );
    // H16 — the same gate for the three pairs that never had one. The court
    // pair and every time-window pair are edited through ONE input inside
    // BookingParametersSegmentedControl (it switches which pair it writes to),
    // so that control reports the error for whichever pair is on screen while
    // the submit gate here has to check ALL of them: a bad value can be left
    // behind on a time window the admin has since navigated away from.
    const coachingSessionDiscountError = validateMembershipDiscount(
        formData.coachingSessionDiscountType,
        formData.coachingSessionDiscountValue,
    );
    const courtBookingDiscountError = validateMembershipDiscount(
        formData.courtBookingDiscountType,
        formData.courtBookingDiscountValue,
    );
    const hasInvalidTimeWindowDiscount = (formData.timeWindowDiscounts ?? []).some(
        (discount) => Boolean(validateMembershipDiscount(
            discount.courtBookingDiscountType,
            discount.courtBookingDiscountValue,
        )),
    );
    // MYS-125 P4.11 — once a type has live variants, D3's sync direction is
    // variant -> type (see the `hasLiveVariants` comment above): the fields
    // this check gates are locked read-only and mirror whatever the
    // `isDefault` variant currently carries, including a legitimate 0€
    // recurring default (a genuinely free, non-Duo variant — D6 only forbids
    // 0€ on `isDuoBeneficiary` rows). Gating Submit on `priceInCents > 0`
    // here permanently disabled it with no visible error (the error branch
    // below is itself suppressed once `hasLiveVariants` is true), recoverable
    // only by promoting a different default via the separate
    // `MembershipVariantsSection` save path. Skip the type-level price check
    // entirely once variants own pricing.
    const isStripeConfigValid = hasLiveVariants
        || !formData.isSubscriptionBased
        || (
            typeof formData.priceInCents === 'number' && formData.priceInCents > 0 &&
            !!formData.currency &&
            !!formData.billingInterval
        );
    const isFormValid = isNameValid
        && isStripeConfigValid
        && !eventJoiningDiscountError
        && !coachingSessionDiscountError
        && !courtBookingDiscountError
        && !hasInvalidTimeWindowDiscount;

    return (
        <>
            <Button colorScheme="brand" onClick={onOpen}>
                {translate('userMembership.add_new_membership_type')}
            </Button>
            <FormModal
                isOpen={isOpen}
                onClose={onModalClose}
                title={selectedMembershipType
                    ? translate('userMembership.edit_membership_type')
                    : translate('userMembership.add_new_membership_type')}
                onSubmit={handleSubmit}
                submitLabel={translate('common.submit')}
                cancelLabel={translate('common.cancel')}
                isSubmitting={isCreating || isUpdating}
                isSubmitDisabled={!isFormValid}
                destructiveAction={
                    selectedMembershipType && !selectedMembershipType.isOrganizationDefault
                        ? {
                              label: translate('common.delete'),
                              onClick: onDeleteOpen,
                          }
                        : undefined
                }
                size="6xl"
            >
                        <Box mt={4}>
                            <FormField
                                type="text"
                                label={translate('userMembership.name')}
                                value={formData.name}
                                onChange={(value) => handleInputChange('name', value)}
                                error={!isNameValid ? translate('userMembership.invalid_membership_type_name') : undefined}
                            />
                        </Box>

                        <Box mt={4}>
                            <FormField
                                type="text"
                                label={translate('product.description')}
                                value={formData.description ?? ''}
                                onChange={(value) => handleInputChange('description', value)}
                                placeholder={translate('product.description_placeholder')}
                            />
                        </Box>

                        <Box mt={4}>
                            <DescriptionSectionsEditor
                                value={formData.descriptionSections}
                                onChange={(next: MembershipDescriptionSection[] | null) =>
                                    handleInputChange('descriptionSections', next)
                                }
                            />
                        </Box>

                        {/* Org default must cover all sports (API enforces it) — hide the field. */}
                        {!selectedMembershipType?.isOrganizationDefault && (
                            <FormControl mt={4}>
                                <FormLabel>{translate('userMembership.sports_label')}</FormLabel>
                                <SportSelect
                                    value={formData.sports ?? []}
                                    onChange={(next: SportType[]) => {
                                        // Drop per-sport overrides for sports no longer covered
                                        const overrides = (formData.sportDiscounts ?? []).filter(
                                            d => next.length === 0 || next.includes(d.sport)
                                        );
                                        setFormData({
                                            ...formData,
                                            sports: next.length > 0 ? next : null,
                                            sportDiscounts: overrides.length > 0 ? overrides : null,
                                        });
                                    }}
                                />
                                <FormHelperText>{translate('userMembership.sports_all_hint')}</FormHelperText>
                            </FormControl>
                        )}

                        {/* Per-sport discount overrides */}
                        <FormControl mt={4}>
                            <FormLabel>{translate('userMembership.sport_discounts_label')}</FormLabel>
                            {(formData.sportDiscounts ?? []).map((override, index) => (
                                <Box key={override.sport} display="flex" alignItems="center" gap={4} mb={2}>
                                    <Select
                                        flex={2}
                                        value={override.sport}
                                        onChange={(e) => {
                                            const next = [...(formData.sportDiscounts ?? [])];
                                            next[index] = { ...override, sport: e.target.value as SportType };
                                            handleInputChange('sportDiscounts', next);
                                        }}
                                    >
                                        {availableOverrideSports(override.sport).map(sport => (
                                            <option key={sport} value={sport}>{sport}</option>
                                        ))}
                                    </Select>
                                    <Input
                                        flex={1}
                                        type="number"
                                        min="0"
                                        value={override.courtBookingDiscountValue}
                                        onChange={(e) => {
                                            const next = [...(formData.sportDiscounts ?? [])];
                                            next[index] = { ...override, courtBookingDiscountValue: parseFloat(e.target.value) || 0 };
                                            handleInputChange('sportDiscounts', next);
                                        }}
                                    />
                                    <Select
                                        flex={1}
                                        value={override.courtBookingDiscountType}
                                        onChange={(e) => {
                                            const next = [...(formData.sportDiscounts ?? [])];
                                            next[index] = { ...override, courtBookingDiscountType: e.target.value as UserMembershipDiscountType };
                                            handleInputChange('sportDiscounts', next);
                                        }}
                                    >
                                        <option value={UserMembershipDiscountType.PERCENTAGE}>%</option>
                                        <option value={UserMembershipDiscountType.CONSTANT}>{translate('userMembership.coachingSessionDiscountType_constant')}</option>
                                    </Select>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        colorScheme="red"
                                        onClick={() => {
                                            const next = (formData.sportDiscounts ?? []).filter((_, i) => i !== index);
                                            handleInputChange('sportDiscounts', next.length > 0 ? next : null);
                                        }}
                                    >
                                        ✕
                                    </Button>
                                </Box>
                            ))}
                            <Button
                                size="sm"
                                variant="outline"
                                isDisabled={availableOverrideSports().length === 0}
                                onClick={() => {
                                    const sport = availableOverrideSports()[0];
                                    if (!sport) return;
                                    handleInputChange('sportDiscounts', [
                                        ...(formData.sportDiscounts ?? []),
                                        {
                                            sport,
                                            courtBookingDiscountType: UserMembershipDiscountType.PERCENTAGE,
                                            courtBookingDiscountValue: 0,
                                        } as SportDiscountDto,
                                    ]);
                                }}
                            >
                                {translate('userMembership.sport_discounts_add')}
                            </Button>
                            <FormHelperText>{translate('userMembership.sport_discounts_hint')}</FormHelperText>
                        </FormControl>

                        <Box mt={4} />
                        <BookingParametersSegmentedControl
                            organizationId={selectedOrganization?.id}
                            maxPrivateCourtBookings={formData.maxPrivateCourtBookings}
                            maxPrivateCourtBookingsDuration={formData.maxPrivateCourtBookingsDuration}
                            maxCourtBookingTimeInAdvance={formData.maxCourtBookingTimeInAdvance}
                            courtBookingDiscountType={formData.courtBookingDiscountType}
                            courtBookingDiscountValue={formData.courtBookingDiscountValue}
                            timeWindowDiscounts={formData.timeWindowDiscounts}
                            onMaxPrivateCourtBookingsChange={(value) => handleInputChange('maxPrivateCourtBookings', value)}
                            onMaxPrivateCourtBookingsDurationChange={(value) => handleInputChange('maxPrivateCourtBookingsDuration', value)}
                            onMaxCourtBookingTimeInAdvanceChange={(value) => handleInputChange('maxCourtBookingTimeInAdvance', value)}
                            onCourtBookingDiscountTypeChange={(value) => handleInputChange('courtBookingDiscountType', value)}
                            onCourtBookingDiscountValueChange={(value) => handleInputChange('courtBookingDiscountValue', value)}
                            onTimeWindowDiscountsChange={(discounts) => handleInputChange('timeWindowDiscounts', discounts)}
                        />

                        <FormControl mt={4} isInvalid={Boolean(coachingSessionDiscountError)}>
                            <FormLabel>{translate('userMembership.coachingSessionDiscountValue')}</FormLabel>
                            <Box display="flex" alignItems="center" justifyContent={'center'} gap={4}>
                                <Input
                                    flex={1}
                                    type="number"
                                    step="1"
                                    min="0"
                                    aria-label={translate('userMembership.coachingSessionDiscountValue')}
                                    // H16 — mirrors the event field below. NaN is a legitimate
                                    // transient state (the admin cleared the input); render it as
                                    // empty instead of handing NaN to the DOM, and let the
                                    // validator block the submit. The old `parseInt` both produced
                                    // that NaN — which serialises to `null` and 500'd an
                                    // `int not null` column — and silently truncated `12.7` to 12.
                                    value={Number.isFinite(formData.coachingSessionDiscountValue as number)
                                        ? formData.coachingSessionDiscountValue
                                        : ''}
                                    onChange={(e) => handleInputChange(
                                        'coachingSessionDiscountValue',
                                        e.target.value === '' ? NaN : Number(e.target.value),
                                    )}
                                />
                                <Select
                                    flex={1}
                                    value={formData.coachingSessionDiscountType}
                                    onChange={(e) => handleInputChange('coachingSessionDiscountType', e.target.value as UserMembershipDiscountType)}>
                                    <option value={UserMembershipDiscountType.PERCENTAGE}>%</option>
                                    <option value={UserMembershipDiscountType.CONSTANT}>{translate('userMembership.coachingSessionDiscountType_constant')}</option>
                                </Select>
                            </Box>
                            {coachingSessionDiscountError ? (
                                <FormErrorMessage>
                                    {translate(MEMBERSHIP_DISCOUNT_ERROR_KEYS[coachingSessionDiscountError])}
                                </FormErrorMessage>
                            ) : null}
                        </FormControl>

                        {/*
                          MYS-122 — event joining discount. Deliberately NOT inside
                          BookingParametersSegmentedControl: that control is scoped
                          per time-window group, and the API has no time-window
                          dimension for event discounts. It mirrors the flat
                          coaching-session discount above instead.
                        */}
                        <FormControl mt={4} isInvalid={Boolean(eventJoiningDiscountError)}>
                            <FormLabel>{translate('userMembership.eventJoiningDiscountValue')}</FormLabel>
                            <Box display="flex" alignItems="center" justifyContent={'center'} gap={4}>
                                <Input
                                    flex={1}
                                    type="number"
                                    step="1"
                                    min="0"
                                    aria-label={translate('userMembership.eventJoiningDiscountValue')}
                                    // NaN is a legitimate transient state (the admin cleared the
                                    // field). Render it as empty rather than passing NaN to the
                                    // DOM, and let the validator block the submit.
                                    value={Number.isFinite(formData.eventJoiningDiscountValue as number)
                                        ? formData.eventJoiningDiscountValue
                                        : ''}
                                    onChange={(e) => handleInputChange(
                                        'eventJoiningDiscountValue',
                                        // Number(), not parseInt(): parseInt('12.7') silently
                                        // truncates to 12, which is exactly the quiet data loss
                                        // this field is meant to surface.
                                        e.target.value === '' ? NaN : Number(e.target.value),
                                    )}
                                />
                                <Select
                                    flex={1}
                                    aria-label={translate('userMembership.eventJoiningDiscountType')}
                                    value={formData.eventJoiningDiscountType ?? UserMembershipDiscountType.PERCENTAGE}
                                    onChange={(e) => handleInputChange('eventJoiningDiscountType', e.target.value as UserMembershipDiscountType)}>
                                    <option value={UserMembershipDiscountType.PERCENTAGE}>%</option>
                                    <option value={UserMembershipDiscountType.CONSTANT}>{translate('userMembership.eventJoiningDiscountType_constant')}</option>
                                </Select>
                            </Box>
                            {eventJoiningDiscountError ? (
                                <FormErrorMessage>
                                    {translate(EVENT_JOINING_DISCOUNT_ERROR_KEYS[eventJoiningDiscountError])}
                                </FormErrorMessage>
                            ) : (
                                <FormHelperText>{translate('userMembership.eventJoiningDiscountValue_hint')}</FormHelperText>
                            )}
                        </FormControl>

                        {/* Subscription / Stripe section */}
                        <Box mt={6} />
                        <FormControl mt={4} isInvalid={!isStripeConfigValid}>
                            <FormLabel>{translate('userMembership.subscription_settings')}</FormLabel>
                            <Box display="flex" alignItems="center" gap={4}>
                                <Select
                                    flex={1}
                                    value={formData.isSubscriptionBased ? 'enabled' : 'disabled'}
                                    onChange={(e) => handleInputChange('isSubscriptionBased', e.target.value === 'enabled')}
                                >
                                    <option value={'disabled'}>{translate('userMembership.subscription_disabled')}</option>
                                    <option value={'enabled'}>{translate('userMembership.subscription_enabled')}</option>
                                </Select>
                                <Input
                                    flex={1}
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder={translate('userMembership.monthly_price_placeholder')}
                                    value={displayPrice}
                                    onChange={(e) => handlePriceChange(e.target.value)}
                                    isDisabled={!formData.isSubscriptionBased || hasLiveVariants}
                                />
                                <Select
                                    flex={1}
                                    value={formData.currency ?? 'eur'}
                                    onChange={(e) => handleInputChange('currency', e.target.value)}
                                    isDisabled={!formData.isSubscriptionBased || hasLiveVariants}
                                >
                                    <option value={Currency.EUR}>EUR</option>
                                    <option value={Currency.USD}>USD</option>
                                </Select>
                                <Select
                                    flex={1}
                                    value={formData.billingInterval ?? MembershipBillingInterval.MONTHLY}
                                    onChange={(e) => handleInputChange('billingInterval', e.target.value as MembershipBillingInterval)}
                                    isDisabled={!formData.isSubscriptionBased || hasLiveVariants}
                                >
                                    <option value={MembershipBillingInterval.MONTHLY}>{translate('userMembership.billing_monthly')}</option>
                                    <option value={MembershipBillingInterval.YEARLY}>{translate('userMembership.billing_yearly')}</option>
                                </Select>
                            </Box>
                            {hasLiveVariants ? (
                                <FormHelperText>
                                    {translate('userMembership.variants_legacy_price_locked_hint')}
                                </FormHelperText>
                            ) : !isStripeConfigValid && (
                                <FormErrorMessage>
                                    {translate('userMembership.invalid_stripe_configuration')}
                                </FormErrorMessage>
                            )}
                        </FormControl>

                        {/* MYS-125 — variant CRUD. Edit mode only: variants POST
                          against a real :typeId, so there is nothing to attach
                          them to before the type itself exists. */}
                        {liveMembershipType && (
                            <MembershipVariantsSection
                                membershipType={liveMembershipType}
                            />
                        )}

                        {/*
                          MYS-117 Duo. Only meaningful on subscription-based
                          plans (beneficiary rows are minted by the payer's
                          subscription lifecycle), so the control is disabled
                          otherwise — and helper copy explains the commercial
                          meaning so operators don't misconfigure (design F13).
                        */}
                        <FormControl mt={4}>
                            <FormLabel>{translate('userMembership.duo_plan_label')}</FormLabel>
                            <Select
                                value={(formData.maxAttachedMembers ?? 0) >= 1 ? 'duo' : 'solo'}
                                onChange={(e) => handleInputChange('maxAttachedMembers', e.target.value === 'duo' ? 1 : 0)}
                                isDisabled={!formData.isSubscriptionBased}
                            >
                                <option value={'solo'}>{translate('userMembership.duo_plan_solo')}</option>
                                <option value={'duo'}>{translate('userMembership.duo_plan_duo')}</option>
                            </Select>
                            <FormHelperText>{translate('userMembership.duo_plan_hint')}</FormHelperText>
                        </FormControl>

                        {/* CGU override — edit mode only; creation happens after the type exists. */}
                        {liveMembershipType && (
                            <MembershipTypeCguOverrideSection
                                membershipType={liveMembershipType}
                            />
                        )}
            </FormModal>

            {/* Delete confirmation */}
            <ConfirmDialog
                isOpen={isDeleteOpen}
                onClose={onDeleteClose}
                onConfirm={handleDelete}
                title={translate('userMembership.delete_membership_type')}
                body={translate('userMembership.delete_membership_type_confirmation')}
                confirmLabel={translate('common.delete')}
                cancelLabel={translate('common.cancel')}
                isConfirming={isDeleting}
            />
        </>
    );
}